import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RoleDeleteDialogComponent } from '../role-delete-dialog/role-delete-dialog.component';
import { RoleService } from '../../../core/services/role.service';
import { RoleUserDTO } from '../../../core/models/role.model';

import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatChipsModule } from '@angular/material/chips';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { AppLoaderComponent } from '../../../components/app-loader.component';

@Component({
  selector: 'app-role-detail',
  imports: [
    MatIconModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressBarModule,
    MatChipsModule,
    MatMenuModule,
    MatDividerModule,
    MatPaginatorModule,
    MatTableModule
],
  templateUrl: './role-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./role-detail.component.scss']
})
export class RoleDetailComponent implements OnInit, OnDestroy {
  role: RoleUserDTO | null = null;
  clientName = '';
  loading = false;
  isRealmRole = false;
  roleName = '';
  clientId = '';

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private roleService: RoleService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.loadRoleDetails();

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Charge les détails du rôle
   */
  private loadRoleDetails(): void {
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.roleName = params['roleName'];
        this.clientId = params['clientId'];
        this.isRealmRole = !this.clientId;

        this.fetchRoleDetails();
      });
  }

  /**
   * Récupère les détails du rôle depuis l'API
   */
  private fetchRoleDetails(): void {
    this.loading = true;

    const observable = this.isRealmRole
      ? this.roleService.getRealmRoleDetails(this.roleName)
      : this.roleService.getClientRoleDetails(this.clientId, this.roleName);

    observable
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (role) => {
          this.role = role;

          if (!this.isRealmRole && role.containerId) {
            this.loadClientName(role.containerId);
          }

          this.loading = false;
        },
        error: (error) => {
          console.error('Erreur lors du chargement du rôle:', error);
          this.showError('Erreur lors du chargement du rôle');
          this.loading = false;
          this.goBack();
        }
      });
  }

  /**
   * Charge le nom du client
   */
  private loadClientName(clientId: string): void {
    this.roleService.getClients()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const client = response.content.find(
            c => c.id === clientId
          );

          this.clientName =
            client?.name ??
            client?.clientId ??
            'Client inconnu';
        },
        error: (error) => {
          console.error('Erreur lors du chargement du client:', error);
          this.clientName = 'Client inconnu';
        }
      });
  }

  /**
   * Navigation vers l'édition
   */
  editRole(): void {
    if (this.isRealmRole) {
      this.router.navigate(['/dashboard/roles/realm', this.roleName, 'edit']);
    } else {
      this.router.navigate(['/dashboard/roles/client', this.clientId, this.roleName, 'edit']);
    }
  }

  /**
   * Ouvre le dialogue de suppression
   */
  openDeleteDialog(): void {
    if (!this.role) return;

    const dialogRef = this.dialog.open(RoleDeleteDialogComponent, {
      width: '450px',
      data: {
        role: this.role,
        clientName: this.clientName
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteRole();
      }
    });
  }

  /**
   * Supprime le rôle
   */
  private deleteRole(): void {
    if (!this.role) return;

    this.loading = true;

    const deleteObservable = this.isRealmRole
      ? this.roleService.deleteRealmRole(this.role.name)
      : this.roleService.deleteClientRole(this.clientId, this.role.name);

    deleteObservable
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showSuccess(`Rôle "${this.role!.name}" supprimé avec succès`);
          this.goBack();
        },
        error: (error) => {
          console.error('Erreur lors de la suppression:', error);
          this.showError('Erreur lors de la suppression du rôle');
          this.loading = false;
        }
      });
  }

  /**
   * Retourne à la liste
   */
  goBack(): void {
    this.router.navigate(['/dashboard/roles']);
  }

  /**
   * Obtient le type de rôle formaté
   */
  getRoleType(): string {
    return this.isRealmRole ? 'Rôle Realm' : 'Rôle Client';
  }

  /**
   * Obtient la couleur du badge de type
   */
  getRoleTypeColor(): string {
    return this.isRealmRole ? '#45B56F' : '#2196f3';
  }

  /**
   * Obtient l'icône du type de rôle
   */
  getRoleTypeIcon(): string {
    return this.isRealmRole ? 'admin_panel_settings' : 'apps';
  }

  /**
   * Génère une couleur d'avatar basée sur le nom
   */
  getAvatarColor(): string {
    if (!this.role) return '#45B56F';

    const colors = [
      '#1abc9c', '#2ecc71', '#3498db', '#9b59b6', '#34495e',
      '#16a085', '#27ae60', '#2980b9', '#8e44ad', '#2c3e50',
      '#f39c12', '#e67e22', '#e74c3c', '#95a5a6', '#d35400'
    ];

    let hash = 0;
    for (let i = 0; i < this.role.name.length; i++) {
      hash = this.role.name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  }

  /**
   * Génère les initiales pour l'avatar
   */
  getInitials(): string {
    if (!this.role) return '?';

    const words = this.role.name.split(/[\s-_]+/);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return this.role.name.substring(0, 2).toUpperCase();
  }

  /**
   * Affiche un message de succès
   */
  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 3000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }

  /**
   * Affiche un message d'erreur
   */
  private showError(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 5000,
      panelClass: ['error-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }
}
